import React, { useEffect, useState, useContext } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Box, Typography, Button, CircularProgress, Alert } from "@mui/material";
import { AuthContext } from "../theme/AuthContext";
import axiosInstance from "../api/axiosInstance";

export default function Checkout() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);

    const courseId = searchParams.get("courseId");

    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [alreadyPurchased, setAlreadyPurchased] = useState(false);

    // 🔹 Fetch Course Details
    useEffect(() => {
        if (!courseId) {
            navigate("/");
            return;
        }

        const fetchCourse = async () => {
            try {
                const res = await axiosInstance.get(
                    `/course/course/${courseId}`,
                    { withCredentials: true }
                );

                console.log("Course Details:", res.data);

                setCourse(res.data.data.metadata);
            } catch (err) {
                console.error(err);
                setError("Failed to load course");
            }
        };

        fetchCourse();
    }, [courseId, navigate]);

    // 🔹 Handle Payment
    const handlePayment = async () => {
        try {
            setLoading(true);
            setError("");

            const token = localStorage.getItem("token");

            // 1. Create Razorpay order from backend
            const res = await axiosInstance.post(
                "/payment/create-order",
                {
                    courseId,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const {
                order_id,
                amount,
                currency,
                key_id,
            } = res.data;

            // 2. Check Razorpay SDK
            if (!window.Razorpay) {
                throw new Error(
                    "Razorpay SDK not loaded. Please refresh the page."
                );
            }

            // 3. Razorpay Checkout options
            const options = {
                key: key_id,
                amount: amount,
                currency: currency,
                name: "Topperhub",
                description: course.title,
                order_id: order_id,

                prefill: {
                    name: user?.name || "",
                    email: user?.email || "",
                    contact: user?.phone || "",
                },

                notes: {
                    courseId: courseId,
                },

                theme: {
                    color: "#667eea",
                },

                // 4. Payment successful
                handler: async function (response) {
                    try {
                        setLoading(true);

                        const verifyResponse = await axiosInstance.post(
                            "/payment/verify",
                            {
                                courseId,

                                razorpay_payment_id:
                                    response.razorpay_payment_id,

                                razorpay_order_id:
                                    response.razorpay_order_id,

                                razorpay_signature:
                                    response.razorpay_signature,
                            },
                            {
                                headers: {
                                    Authorization: `Bearer ${token}`,
                                },
                            }
                        );

                        console.log(
                            "Payment verification response:",
                            verifyResponse.data
                        );

                        if (verifyResponse.data?.success) {

                            // Payment + verification successful
                            navigate(
                                `/payment-success?courseId=${courseId}`
                            );

                        } else {

                            // Verification failed
                            navigate(
                                `/payment-failure?courseId=${courseId}`
                            );
                        }

                    } catch (err) {

                        console.error(
                            "Payment verification error:",
                            err
                        );

                        navigate(
                            `/payment-failure?courseId=${courseId}`
                        );

                    } finally {
                        setLoading(false);
                    }
                },

                // 5. User closes Razorpay window
                modal: {
                    ondismiss: function () {
                        setLoading(false);
                    },
                },
            };

            // 6. Create Razorpay instance
            const razorpay = new window.Razorpay(options);

            // 7. Payment failed
            razorpay.on("payment.failed", function (response) {
                console.error(
                    "Razorpay Payment Failed:",
                    response.error
                );

                setLoading(false);

                navigate(
                    `/payment-failure?courseId=${courseId}`
                );
            });

            // 8. Open Razorpay Checkout
            razorpay.open();

        } catch (err) {
            console.error("Create Razorpay Order Error:", err);

            if (err.response) {
                const message = err.response.data?.message;

                if (
                    err.response.status === 400 &&
                    message === "Course already purchased"
                ) {
                    setAlreadyPurchased(true);
                    setError("");
                } else {
                    setError(
                        message ||
                        "Unable to initiate payment."
                    );
                }
            } else {
                setError(
                    err.message ||
                    "Network error. Please try again."
                );
            }

            setLoading(false);
        }
    };

    // 🔹 Safety: not logged in
    useEffect(() => {
        if (!user) {
            navigate(`/login?courseId=${courseId}`);
        }
    }, [user, courseId, navigate]);

    if (!course) {
        return (
            <Box textAlign="center" mt={10}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#f6f9fc",
                p: 2,
            }}
        >
            <Box
                sx={{
                    width: "100%",
                    maxWidth: "900px",
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    borderRadius: "16px",
                    overflow: "hidden",
                    boxShadow: "0 20px 60px rgba(0,0,0,0.1)",
                    background: "#fff",
                }}
            >
                {/* LEFT: Course Info */}
                <Box
                    sx={{
                        p: 4,
                        background: "linear-gradient(135deg, #667eea, #764ba2)",
                        color: "#fff",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                    }}
                >
                    <Box>
                        <Typography variant="h5" fontWeight="bold" mb={2}>
                            {course.title}
                        </Typography>

                        <Typography sx={{ opacity: 0.9 }}>
                            {course.description}
                        </Typography>
                    </Box>

                    <Box mt={4}>
                        <Typography variant="h3" fontWeight="bold">
                            ₹{course.selling}
                        </Typography>

                        <Typography sx={{ opacity: 0.8, mt: 1 }}>
                            One-time payment
                        </Typography>
                    </Box>
                </Box>

                {/* RIGHT: Payment Section */}
                <Box sx={{ p: 4 }}>
                    <Typography variant="h6" fontWeight="bold" mb={3}>
                        Payment Details
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {alreadyPurchased && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            You're already enrolled in this course.
                        </Alert>
                    )}

                    {/* Fake Card UI (for design only) */}
                    <Box
                        sx={{
                            border: "1px solid #e0e0e0",
                            borderRadius: "10px",
                            p: 2,
                            mb: 3,
                            background: "#fafafa",
                        }}
                    >
                        <Typography fontSize="14px" color="text.secondary">
                            Secure payment powered by Razorpay
                        </Typography>
                    </Box>

                    <Button
                        variant="contained"
                        fullWidth
                        onClick={alreadyPurchased ? () => navigate("/courses") : handlePayment}
                        disabled={loading}
                        sx={{
                            py: 1.5,
                            fontSize: "16px",
                            fontWeight: 600,
                            borderRadius: "8px",
                            textTransform: "none",
                        }}
                    >
                        {loading ? (
                            <CircularProgress size={24} sx={{ color: "#fff" }} />
                        ) : alreadyPurchased ? (
                            "Continue Learning"
                        ) : (
                            `Pay ₹${course.selling}`
                        )}
                    </Button>

                    {/* Trust Footer */}
                    <Typography
                        variant="caption"
                        display="block"
                        textAlign="center"
                        mt={2}
                        color="text.secondary"
                    >
                        🔒 Secure checkout powered by Razorpay
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}